import classNames from "classnames";

import Database from "~/lib/Database";
import { getRelativeDateString } from "~/lib/date";
import { track } from "~/lib/segmentAnalytics";
import BuildPageStore from "~/stores/BuildPageStore";
import Button from "~/components/shared/Button";
import Dialog from "~/components/shared/Dialog";
import {
  HoverCard,
  HoverCardContent,
  HoverCardPortal,
  HoverCardTrigger,
} from "~/components/shared/HoverCard";
import Icon from "~/components/shared/Icon";
import LegacyDispatcher from "~/lib/legacyDispatcher";
import parseEmoji from "~/lib/parseEmoji";
import PipelineStateIcon, {
  colorForJob,
} from "~/components/shared/PipelineStateIcon/PipelineStateIcon";
import RemoteButtonComponent from "~/components/shared/RemoteButtonComponent";
import { useStepDependencies } from "~/components/build/Show/lib/useStepDependencies";
import UserAvatar from "~/components/shared/UserAvatar";

import Agent from "~/components/job/Job/Agent";
import ClusterQueue from "~/components/job/Job/ClusterQueue";
import Command from "~/components/job/Job/Command";
import { getDefaultJobTab, getJobTabs, JobTab, isTabAvailable } from "~/components/job/Job/jobTabs";
import { JobDuration } from "~/components/job/Job/JobDuration";
import { LimitedByConcurrencyGroup } from "~/components/job/Job/LimitedByConcurrencyGroup";
import { LimitedByPlatform } from "~/components/job/Job/LimitedByPlatform";
import LogWarnings from "~/components/job/Job/LogWarnings";
import { MissingDependenciesNode } from "~/components/build/Show/components/MissingDependenciesNode";
import { MissingStepDependencies } from "~/components/job/Job/MissingStepDependencies";
import SchedulerErrorNotice from "~/components/job/SchedulerErrorNotice";
import Signature from "~/components/job/Job/Signature";
import StackFinished from "~/components/job/Job/StackFinished";
import { StackErrored } from "~/components/job/Job/StackErrored";

import { isTerminalBuildState } from "~/constants/BuildStates";
import { twMerge } from "tailwind-merge";
import { Link } from "react-router-dom";
import { resolveJobUrl } from "~/components/build/Show/lib/urlForView";
import { Job as JobType, Build, CommandJob } from "~/components/build/Show/lib/types";
import { PureComponent, ReactNode } from "react";

export const elementIdFor = (jobId) => `job-${jobId}`;

export const TERMINAL_JOB_STATES = ["finished", "timed_out", "canceled", "expired"];
export const NOT_YET_SCHEDULED_STATES = [
  "pending",
  "broken",
  "blocked_failed",
  "waiting_failed",
  "blocked",
  "waiting",
  "limiting",
  "limited",
];

/**
 * Wrapper that enriches the job with missing dependencies from the Zustand store,
 * since the job object from the API doesn't include step dependency data.
 */
const JobStateIcon = ({ job, build }: { job: JobType; build: Build }) => {
  const { missingDependencies } = useStepDependencies(job.stepUuid);
  const jobWithDeps = { ...job, missingDependencies };
  return <PipelineStateIcon job={jobWithDeps} className={colorForJob(jobWithDeps, build)} />;
};

function DisabledRetryJobButton({ message, children }: { message: string; children: ReactNode }) {
  return (
    <HoverCard>
      <HoverCardTrigger asChild={true}>
        <span className="inline-flex cursor-not-allowed" data-testid="disabled-retry-job-button">
          <button
            type="button"
            disabled={true}
            className="btn btn-default btn-small btn-flex pointer-events-none opacity-60"
            aria-disabled="true"
            style={{ fontSize: 13, height: 34, lineHeight: "13px" }}
          >
            {children}
          </button>
        </span>
      </HoverCardTrigger>
      <HoverCardPortal>
        <HoverCardContent side="top" align="end" sideOffset={8} size="md" className="z-50 text-sm">
          <div className="font-medium">Retry disabled</div>
          <div className="mt-1 text-gray-600">{message}</div>
        </HoverCardContent>
      </HoverCardPortal>
    </HoverCard>
  );
}

export type Props = {
  autoFollow?: boolean;
  className?: string;
  build: Build;
  setBuildData: (build: Build) => void;
  job: JobType & {
    testEngine: {
      testTabUrls?: {
        tabUrl: string | null;
        failureIndicatorUrl: string | null;
      };
      runUrl?: string;
    };
    packages?: {
      indexUrl?: string;
    } | null;
    hostedAgentMetricsPath?: string;
    concurrency?: number;
    concurrencyGroup?: string;
    upgradePlanPath?: string;
    projectProviderId?: string;
    agentOsId?: string | null;
    cluster?: { id: string };
  };
  handleClickHeader?: (latestJobUUID: string) => void;
  onRetrySuccess?: (latestJobUUID: string, latestJob?: JobType) => void;
  activeTab?: string | null;
  onTabChange?: (tabId: string) => void;
};

type State = {
  expanded: boolean;
  cancelJobDialogOpen: boolean;
  tab?: string;
};

export default class Job extends PureComponent<Props, State> {
  static displayName: "Job";

  constructor(props: Props) {
    super(props);

    this.state = {
      tab: props.activeTab || getDefaultJobTab(props.job),
      expanded: this.suggestedExpandedState(),
      cancelJobDialogOpen: false,
    };

    // bind all the thises.
    this._onStoreChange = this._onStoreChange.bind(this);
    this.suggestedExpandedState = this.suggestedExpandedState.bind(this);
    this.handleClickHeader = this.handleClickHeader.bind(this);
    this.handleClickRetriedJobLink = this.handleClickRetriedJobLink.bind(this);
    this.handleCancelJobClick = this.handleCancelJobClick.bind(this);
    this.handleCancelJobDialogClose = this.handleCancelJobDialogClose.bind(this);
    this.handleTabClick = this.handleTabClick.bind(this);
  }

  tabs() {
    return getJobTabs(this.props.job);
  }

  // Some magic to determine the default expanded state for the job.
  suggestedExpandedState() {
    return BuildPageStore.isJobExpanded(this.props.job);
  }

  componentDidMount() {
    BuildPageStore.addChangeListener(this.props.job, this._onStoreChange);
  }

  componentWillUnmount() {
    BuildPageStore.removeChangeListener(this.props.job, this._onStoreChange);
  }

  componentDidUpdate(prevProps: Props) {
    // Sync tab state when activeTab changes
    if (prevProps.activeTab !== this.props.activeTab && this.props.activeTab) {
      this.setState({ tab: this.props.activeTab });
    }
  }

  wrapperNode;

  _onStoreChange() {
    const expanded = BuildPageStore.isJobExpanded(this.props.job);

    // If we're changing from expanded to not exapnded, and the wrapping
    // element is outside the viewport (meaning the header is likely stickied
    // to the top of it), scroll to the top of the element so it stays in view
    // when it collapses. This is for Sticky Job Headers, but shouldn't change
    // much of note about the page's behaviour when that isn't enabled.
    if (this.state.expanded && !expanded && this.wrapperNode.offsetTop < window.scrollY) {
      this.wrapperNode.scrollIntoView();
    }

    this.setState({ expanded });
  }

  handleCancelJobClick() {
    this.setState({ cancelJobDialogOpen: true });
  }

  handleCancelJobDialogClose() {
    this.setState({ cancelJobDialogOpen: false });
  }

  handleTabClick(tab) {
    return (event) => {
      this.setState({ tab: tab.id });

      // Execute callback function if provided
      this.props.onTabChange?.(tab.id);

      // If the browser viewport is below the top of the job component, scroll back to the top of the job component.
      if (this.wrapperNode.offsetTop < window.scrollY) {
        this.wrapperNode.scrollIntoView();
      }

      event.preventDefault();

      const startedAt = (
        (this.props.job.type === "script" || this.props.job.type === "trigger") &&
        this.props.job.startedAt
          ? new Date(this.props.job.startedAt)
          : new Date()
      ).getTime(); // convert Date to integet with `.getTime()` so we can do slightly better sums
      const now = new Date().getTime();
      const timeSinceJobStarted = now - startedAt;
      const daysSinceJobStarted = Math.floor(timeSinceJobStarted / 1000 / 60 / 60 / 24);

      track("Job Tab Clicked", {
        tab_name: tab.id,
        job_id: this.props.job.id,
        job_state: this.props.job.state,
        days_since_job_started: daysSinceJobStarted,
      });
    };
  }

  handleClickHeader(evt) {
    if (Features.BuildSidebar) {
      if (this.props.handleClickHeader) {
        this.props.handleClickHeader(this.props.job.id);
        // @ts-expect-error - TS2554 - Expected 6 arguments, but got 2.
        LegacyDispatcher.emit("job:toggle", { job: this.props.job });
      }

      // Return early to avoid altering the url in new build ui
      return;
    }

    window.location.href =
      window.location.pathname + `#${this.state.expanded ? "_" : this.props.job.id}`;

    // return if the user did a middle-click, right-click, or used a modifier
    // key (like ctrl-click, meta-click, shift-click, etc.)
    if (
      (evt.button && evt.button !== 0) ||
      evt.altKey ||
      evt.ctrlKey ||
      evt.metaKey ||
      evt.shiftKey
    ) {
      return;
    }
    // @ts-expect-error - TS2554 - Expected 6 arguments, but got 2.
    LegacyDispatcher.emit("job:toggle", { job: this.props.job });
  }

  handleClickRetriedJobLink(evt) {
    // return if the user did a middle-click, right-click, or used a modifier
    // key (like ctrl-click, meta-click, shift-click, etc.)
    if (evt.button !== 0 || evt.altKey || evt.ctrlKey || evt.metaKey || evt.shiftKey) {
      return;
    }

    // @ts-expect-error - TS2554 - Expected 6 arguments, but got 2.
    LegacyDispatcher.emit("job:retried-link-click", { job: this.props.job });
  }

  tabMenuNodes() {
    return this.tabs().map((tab) => {
      let className = "btn btn-tab btn-tab--compact btn-tab--monochrome";

      if (Features.BuildSidebar) {
        className += " px-2";
      }

      if (this.state.tab === tab.id) {
        className += " btn-tab--active";
      }

      if (tab.count) {
        className += " btn-tab--badged";
      }

      if (tab.indicator) {
        className += " relative";
      }

      return (
        <li key={tab.id} className="flex items-stretch">
          {this._renderTabLink(tab, className)}
        </li>
      );
    });
  }

  _renderTabLink(tab: JobTab, className: string) {
    const TabCountComponent = tab.count;
    const TabIndicatorComponent = tab.indicator;
    const tabName =
      typeof tab.name === "function" ? tab.name(this.props.job as CommandJob) : tab.name;

    return (
      <button className={className} onClick={this.handleTabClick(tab)}>
        <span className="relative">
          {tabName}
          {TabIndicatorComponent && (
            <TabIndicatorComponent
              build={this.props.build}
              job={this.props.job}
              translateX="75%"
              translateY="-50%"
            />
          )}
        </span>
        {TabCountComponent && <TabCountComponent build={this.props.build} job={this.props.job} />}
        {tab.new && this._renderNewBadge()}
      </button>
    );
  }

  _renderNewBadge() {
    return <span className="badge badge--outline tabular-numerals ml-1">New</span>;
  }

  tabContentNodes() {
    return this.tabs().map((tab) => {
      if (this.state.tab === tab.id) {
        const contents = (
          <tab.component
            build={this.props.build}
            job={this.props.job}
            autoFollow={this.props.autoFollow}
            parentElementId={this.elementId()}
          />
        );

        return (
          <div
            key={tab.id}
            className={classNames("tab-pane", {
              active: this.state.tab === tab.id,
              "px-2.5":
                Features.BuildSidebar &&
                !(this.state.tab === "output" || this.state.tab === "agent-log"),
              "!flex min-h-0 flex-auto flex-col": Features.BuildSidebar,
            })}
            data-tab={tab.id}
          >
            {contents}
          </div>
        );
      }
    });
  }

  groupInfoNode() {
    let text: string | null = null;

    // must be a CommandJob
    if (this.props.job.type !== "script") {
      return null;
    }

    if (this.props.job.matrix) {
      text = "Matrix";
    } else if (
      this.props.job.parallelGroupTotal &&
      this.props.job.parallelGroupTotal > 1 &&
      this.props.job.parallelGroupIndex !== undefined
    ) {
      text = `${this.props.job.parallelGroupIndex + 1}/${this.props.job.parallelGroupTotal}`;
    }

    if (text) {
      return (
        <div
          className="small tabular-numerals job-group-count relative flex flex-none items-center px-1"
          style={{ lineHeight: 1.75 }}
        >
          {text}
        </div>
      );
    }
  }

  commandNode() {
    if (this.props.job.type === "script" && this.props.job.command) {
      return (
        <Command
          command={this.props.job.command}
          onNestedLinkTrigger={this.handleNestedLinkTrigger}
        />
      );
    }
  }

  jobNameNode() {
    if (this.props.job.name) {
      return (
        <span
          onClick={window.Features.BuildsHighlightJobName ? this.handleClickNode : undefined}
          className="job-name max-w-full truncate"
          style={{ flex: "0 0 auto" }}
          data-testid="JobName"
          dangerouslySetInnerHTML={{ __html: parseEmoji(this.props.job.name) }}
        />
      );
    }
  }

  signatureNode() {
    if (
      this.props.job.type === "script" &&
      this.props.job.step?.signature &&
      this.props.job.step.signature.algorithm
    ) {
      return (
        <Signature
          algorithm={this.props.job.step.signature.algorithm}
          onNestedLinkTrigger={this.handleNestedLinkTrigger}
        />
      );
    }
  }

  elementId() {
    return elementIdFor(this.props.job.id);
  }

  renderTestAnalyticsButton() {
    const { runUrl } = this.props.job.testEngine;
    // These flags are only set for authenticated users with TA
    const runLinkEnabled = Features.Analytics;

    // runUrl returns as null when the run suite is not public, the job doesn't have an
    // associated run, or if the associated run didn't have the job id sent in the run env
    if (runUrl && (runLinkEnabled || !Features.AnalyticsPublicSuiteEmergencyStop)) {
      return (
        <Button href={runUrl} className="btn-small btn-flex">
          View in Test Engine
        </Button>
      );
    }
  }

  renderSSHButton() {
    if (this.props.job.type !== "script") {
      return null;
    }

    const running = this.props.job.state === "running";
    const hostedAgentSSHPath = this.props.job.hostedAgentSshPath;

    if (running && hostedAgentSSHPath) {
      return (
        <Button
          href={hostedAgentSSHPath}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-small btn-flex"
        >
          <Icon icon="heroicons/20/solid/command-line" className="mr-1 h-5 w-5" />
          Open Terminal
          <Icon icon="heroicons/16/solid/arrow-top-right-on-square" className="ml-1 h-3 w-3" />
        </Button>
      );
    }
  }

  renderRetryJobButton() {
    if (this.props.job.type !== "script") {
      return null;
    }

    // Before attempting to render the button, we'll see if we've already
    // retried this job, and if we have, render who did it and when.
    let retryNotAllowedMessage;
    if (this.props.job.retriedInJobUuid && this.props.job.retriedAt) {
      const linkContents = this.props.job.retriedBy ? (
        <>
          <UserAvatar
            user={{
              name: this.props.job.retriedBy.name,
              avatar: { url: this.props.job.retriedBy.avatarUrl },
            }}
            className="mx-1"
            style={{
              width: 20,
              height: 20,
            }}
            suppressAltText={true}
          />
          <span>
            <span className="semi-bold">{this.props.job.retriedBy.name}</span>
            &nbsp;retried {getRelativeDateString(this.props.job.retriedAt)}
          </span>
        </>
      ) : (
        <>
          <Icon
            icon="retry"
            className="flex-none"
            style={{
              width: 14,
              height: 14,
              position: "relative",
              top: -1,
              marginLeft: 3,
              marginRight: 3,
            }}
          />
          Automatically retried {getRelativeDateString(this.props.job.retriedAt)}
        </>
      );

      return (
        <Link
          to={
            Features.BuildSidebar
              ? resolveJobUrl(this.props.job.retriedInJobUuid)
              : window.location.pathname + `#${this.props.job.retriedInJobUuid}`
          }
          onClick={this.handleClickRetriedJobLink}
          className="text-decoration-underline black hover-black block flex items-center py-1 max-md:sr-only"
          style={{ fontSize: 13 }}
        >
          {linkContents}
        </Link>
      );
    }

    // We never want to show the retry button if the current user is anonymous,
    // so we'll just bail out early here and return nothing.
    if (this.props.job.permissions.retry.reason === "anonymous") {
      return null;
    }

    // Don't show the retry button if it's not finished.
    if (!TERMINAL_JOB_STATES.includes(this.props.job.state)) {
      return null;
    }

    // Also don't show the retry button if the job has "passed" and there isn't
    // an error message (we may want to show the retry button for failed jobs if
    // the user has configured it to be allowed)
    if (this.props.job.passed && !this.props.job.retryable.allowed) {
      return null;
    }

    // First check if we're allowed to retry based on permissions. If we're
    // all good on that front, check to see if the job is eligible for
    // retries.
    if (!this.props.job.permissions.retry.allowed) {
      retryNotAllowedMessage = this.props.job.permissions.retry.message;
    } else if (!this.props.job.retryable.allowed) {
      retryNotAllowedMessage = this.props.job.retryable.message;
    }

    const retryButtonContent = (
      <>
        <Icon icon="retry" className="mr-1 size-3" />
        Retry
      </>
    );

    if (retryNotAllowedMessage) {
      return (
        <DisabledRetryJobButton message={retryNotAllowedMessage}>
          {retryButtonContent}
        </DisabledRetryJobButton>
      );
    }

    const options = {
      url: this.props.job.basePath + "/retry",
      method: "post",
      loadingText: "Retrying...",
      className: "btn btn-default btn-small btn-flex",
      onSuccess: (_event, response) => {
        if (response && response.build) {
          this.props.setBuildData(response.build);
          this.props.onRetrySuccess &&
            this.props.onRetrySuccess(
              response.latest_job_uuid,
              response.latest_job ? Database.parse(response.latest_job) : undefined,
            );
        }
      },
      onError: (_event, response) => {
        if (response && response.message) {
          alert(response.message);
        }
      },
    };

    // Retries are all good! Let's render the button.
    return <RemoteButtonComponent {...options}>{retryButtonContent}</RemoteButtonComponent>;
  }

  renderCancelJobDialog() {
    if (this.props.job.type !== "script") {
      return null;
    }

    const options = {
      url: this.props.job.basePath + "/cancel",
      method: "post",
      loadingText: "Canceling...",
      className: "btn btn-danger btn-small btn-flex",
      onSuccess: (event, response) => {
        if (response && response.build) {
          this.handleCancelJobDialogClose();
          this.props.setBuildData(response.build);
        }
      },
      onError: (event, response) => {
        if (response && response.message) {
          alert(response.message);
        }
      },
    };

    return (
      <Dialog
        isOpen={this.state.cancelJobDialogOpen}
        onRequestClose={this.handleCancelJobDialogClose}
        width={400}
      >
        <div className="px-5 pb-5">
          <h2 className="text-xl">Are you sure?</h2>
          <p className="mb-3">
            Canceling this job may have an impact on the success of the overall build. Continue?
          </p>
          <RemoteButtonComponent {...options}>Yes, Cancel Job</RemoteButtonComponent>
        </div>
      </Dialog>
    );
  }

  renderCancelJobButton() {
    // First check if we're allowed to cancel based on permissions.
    // If not, we'll just bail out early here and return nothing.

    if (this.props.job.type !== "script" || !this.props.job.permissions.cancel.allowed) {
      return null;
    }

    const options = {
      className: "btn btn-danger btn-small btn-flex",
      disabled: false,
      loading: "",
    };

    if (this.props.job.state === "canceling") {
      options.disabled = true;
      options.loading = "Canceling...";
    } else if (!this.props.job.cancelable?.allowed) {
      // Don't render the cancel button if it can't be cancelled
      return null;
    }

    return (
      <Button {...options} onClick={this.handleCancelJobClick}>
        Cancel
      </Button>
    );
  }

  renderMissingDependencies() {
    return (
      <MissingStepDependencies
        stepUuid={this.props.job.stepUuid}
        variant={isTerminalBuildState(this.props.build?.state) ? "error" : "warning"}
      />
    );
  }

  renderMissingGroupDependencies() {
    if (!this.props.job.groupUuid) {
      return null;
    }

    return (
      <MissingStepDependencies
        stepUuid={this.props.job.groupUuid}
        group={true}
        variant={isTerminalBuildState(this.props.build?.state) ? "error" : "warning"}
      />
    );
  }

  renderLimitedBy() {
    if (
      this.props.job.state !== "limited" ||
      !(this.props.job.concurrency && this.props.job.concurrencyGroup)
    ) {
      return null;
    }

    return (
      <LimitedByConcurrencyGroup
        concurrencyGroup={this.props.job.concurrencyGroup}
        concurrency={this.props.job.concurrency}
        build={this.props.build}
        job={this.props.job}
      />
    );
  }

  renderSchedulerErrorNotice() {
    return <SchedulerErrorNotice job={this.props.job} />;
  }

  renderLogWarnings() {
    if (this.props.job.type !== "script") {
      return null;
    }

    return <LogWarnings job={this.props.job} build={this.props.build} />;
  }

  renderPlatformLimited() {
    if (this.props.job.state !== "platform_limited") {
      return null;
    }

    return <LimitedByPlatform build={this.props.build} job={this.props.job} />;
  }

  renderStackError() {
    if (this.state.tab === "timeline") {
      return null;
    }
    return <StackErrored job={this.props.job} />;
  }

  renderStackFinished() {
    if (this.state.tab === "timeline") {
      return null;
    }
    return <StackFinished job={this.props.job} />;
  }

  render() {
    const { className, job } = this.props;

    let state;
    if (TERMINAL_JOB_STATES.includes(job.state)) {
      switch (true) {
        case job.state === "canceled" &&
          (job.type === "script" || job.type === "trigger") &&
          job.passed:
          state = "canceled-passed";
          break;
        case job.state === "canceled":
          state = "canceled";
          break;
        case (job.type === "script" || job.type === "trigger") && job.passed:
          state = "passed";
          break;
        case job.softFailed:
          state = "soft_failed";
          break;
        default:
          state = "failed";
      }
    } else {
      state = job.state;
    }

    const jobContentNode = this.state.expanded ? (
      <div
        className={classNames({
          "build-details-pipeline-job-body z-[1] px-[15px]": !Features.BuildSidebar,
          "flex min-h-0 flex-auto flex-col": Features.BuildSidebar,
        })}
      >
        <div
          className={classNames("flex flex-col gap-2 has-[*]:pt-4", {
            "mx-4": Features.BuildSidebar,
          })}
        >
          {this.renderMissingDependencies()}
          {this.renderMissingGroupDependencies()}
          {this.renderLimitedBy()}
          {this.renderPlatformLimited()}
          {this.renderSchedulerErrorNotice()}
          {this.renderStackError()}
          {this.renderStackFinished()}
          {this.renderLogWarnings()}
        </div>

        <div
          className={classNames({
            "build-details-pipeline-job-body__controls border-gray z-[2] mb-0 flex border-b bg-white pt-2":
              !Features.BuildSidebar,
            "no-scrollbar flex shrink-0 bg-white pt-1.5": Features.BuildSidebar,
          })}
          style={{ overflowX: "auto", overflowY: "hidden" }}
        >
          <div
            className={classNames("flex flex-auto shrink-0", {
              "border-gray border-b": Features.BuildSidebar,
            })}
          >
            <div
              className={classNames(
                "border-gray sticky right-0 z-10 order-1 -mb-[1px] -mt-1.5 flex border-b bg-white pr-2.5",
                {
                  "build-details-pipeline-job-body__actions": !Features.BuildSidebar,
                },
              )}
            >
              <div className="flex items-center gap-1 bg-white">
                {this.renderSSHButton()}
                {this.renderTestAnalyticsButton()}
                {this.renderRetryJobButton()}
                {this.renderCancelJobButton()}
              </div>
            </div>
            <ul
              className={classNames(
                "monochrome order-none m-0 flex min-w-0 flex-auto items-stretch p-0",
                {
                  "px-2.5": Features.BuildSidebar,
                },
              )}
              style={{ fontSize: 13 }}
            >
              {this.tabMenuNodes()}
            </ul>
          </div>
        </div>
        <div
          id="tab-scroll-container"
          key={this.state.tab}
          className={classNames("tab-content", {
            "flex min-h-0 flex-auto flex-col overflow-auto": Features.BuildSidebar,
          })}
        >
          {this.tabContentNodes()}
        </div>
      </div>
    ) : null;

    return (
      <>
        <div
          className={classNames(
            `build-details-pipeline-job relative build-details-pipeline-job-state-${state}`,
            {
              "build-details-pipeline-job-expanded": this.state.expanded,
              "!mb-0 flex min-h-0 flex-auto flex-col": Features.BuildSidebar,
            },
            className,
          )}
          id={this.elementId()}
          ref={(wrapperNode) => (this.wrapperNode = wrapperNode)}
        >
          <div
            role="button"
            tabIndex={0}
            className={twMerge(
              classNames(
                "build-details-pipeline-job__header color-inherit focus-color-inherit hover-color-inherit z-[2] flex cursor-pointer items-center gap-x-2.5 bg-white px-3 py-3.5 no-underline antialiased",
                {
                  "focus-bg-silver hover-bg-silver": !Features.BuildSidebar,
                  "cursor-default": Features.BuildSidebar && !this.props.handleClickHeader,
                  "!top-0 !rounded-none !border-x-0 !border-t-gray-400 !py-2.5 px-2.5":
                    Features.BuildSidebar,
                },
              ),
            )}
            onClick={this.handleClickHeader}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                this.handleClickHeader(event);
              }
            }}
            aria-pressed={this.state.expanded}
          >
            <div
              className={classNames("flex min-w-0 flex-auto items-center gap-x-1.5", {
                "pl-1.5": Features.BuildSidebar,
              })}
            >
              <span className="build-details-pipeline-icon">
                <JobStateIcon job={job} build={this.props.build} />
              </span>
              {this.groupInfoNode()}
              <span className="flex min-w-0 flex-auto items-center gap-x-1.5">
                {this.jobNameNode()}
                {this.commandNode()}
              </span>
            </div>
            {this.specialStateNode() ??
              ((job.type === "script" || job.type === "trigger") && (
                <MissingDependenciesNode job={job} build={this.props.build} />
              ))}

            {job.type === "script" || job.type === "trigger" ? (
              <JobDuration
                job={job}
                build={this.props.build}
                className="job-stats"
                hostedConcurrency={job.type === "script" ? job.hostedConcurrency : undefined}
                upgradePlanPath={this.props.job.upgradePlanPath}
              />
            ) : null}

            {!isTabAvailable(job, "agent") && (
              <>
                {this.agentNode()}
                {this.clusterQueueNode()}
              </>
            )}

            {this.signatureNode()}
          </div>
          {jobContentNode}
        </div>
        {this.renderCancelJobDialog()}
      </>
    );
  }

  handleClickNode(event) {
    const selection = window.getSelection();
    const range = document.createRange();

    range.selectNodeContents(event.currentTarget);
    selection?.removeAllRanges();
    selection?.addRange(range);
  }

  agentNode() {
    const hostedQueue = this.props.job.hosted;
    const instanceShape =
      this.props.job.type === "script" ? this.props.job.instanceShape : undefined;
    const hostedOSVersion =
      this.props.job.type === "script" ? this.props.job.hostedOSVersion : undefined;

    if (this.props.job.agentName) {
      return (
        <div className="flex-none max-md:sr-only">
          <Agent
            agentName={this.props.job.agentName}
            agentPath={this.props.job.agentPath}
            queueName={this.props.job.clusterQueue?.key}
            agentsPath={this.props.job.agentsPath}
            onNestedLinkTrigger={this.handleNestedLinkTrigger}
            hosted={hostedQueue}
            instanceShape={instanceShape}
            hostedOSVersion={hostedOSVersion}
          />
        </div>
      );
    }
  }

  specialStateNode() {
    let text: string | null = null;

    if (this.props.job.state === "canceled") {
      text = "Canceled";
    } else if (this.props.job.state === "canceling") {
      text = "Canceling…";
    } else if (this.props.job.state === "timed_out") {
      text = "Timed Out";
    } else if (this.props.job.state === "timing_out") {
      text = "Timing Out…";
    } else if (this.props.job.state === "expired") {
      text = "Expired";
    }

    if (text) {
      return (
        <div className="job-stats gap1 flex flex-none items-center max-md:sr-only">
          <Icon
            icon="heroicons/16/solid/exclamation-triangle"
            className="shrink-0"
            style={{ width: "14px", height: "14px" }}
          />

          <span>{text}</span>
        </div>
      );
    }

    return null;
  }

  handleNestedLinkTrigger(event) {
    // The job header is a clickable container that expands the job details. We sometimes nest links
    // within this header, and when they're triggered we don't want the expanded state to change, so
    // we'll prevent event propagation here.
    if (event.type === "click" || (event.type === "keydown" && event.key === "Enter")) {
      event.stopPropagation();
    }
  }

  clusterQueueNode() {
    if (!this.props.job.agentsPath) {
      return null;
    }

    const queueName = this.props.job.clusterQueue?.key;
    const hasAgent = this.props.job.agentName;

    // Don't render if the job hasn't been scheduled yet.
    if (NOT_YET_SCHEDULED_STATES.includes(this.props.job.state)) {
      return null;
    }

    // Don't render once we have an agent
    if (hasAgent) {
      return null;
    }

    return (
      <div className="flex-none max-md:sr-only">
        <ClusterQueue
          queueName={queueName}
          agentsPath={this.props.job.agentsPath}
          onNestedLinkTrigger={this.handleNestedLinkTrigger}
        />
      </div>
    );
  }
}
